import React, { useCallback } from 'react';
import { Button, InputNumber, Input, Table, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FH141 } from '../../../services/pwh204Api';

interface DetailGridProps {
  details: Partial<FH141>[];
  isEditing: boolean;
  onChange: (details: Partial<FH141>[]) => void;
}

const DetailGrid: React.FC<DetailGridProps> = ({ details, isEditing, onChange }) => {
  const handleAdd = useCallback(() => {
    const newRow: Partial<FH141> = {
      fh141_02: details.length + 1,
      fh141_11: '',
      fh141_12: '',
      fh141_13: 0,
      fh141_14: 0,
      fh141_15: 0,
      fh141_16: '',
      fh141_17: 0,
      fh141_18: '',
      fh141_19: 0,
      fh141_90: 0,
    };
    onChange([...details, newRow]);
  }, [details, onChange]);

  const handleDelete = useCallback((idx: number) => {
    const updated = details.filter((_, i) => i !== idx).map((d, i) => ({ ...d, fh141_02: i + 1 }));
    onChange(updated);
  }, [details, onChange]);

  const handleChange = useCallback((idx: number, field: keyof FH141, value: any) => {
    const updated = [...details];
    const row = { ...updated[idx], [field]: value };

    // BR-C03: auto-calculate tax and amount when net changes
    if (field === 'fh141_13') {
      const net = typeof value === 'number' ? value : 0;
      const tax = Math.round(net * 0.05);
      row.fh141_14 = tax;
      row.fh141_17 = net + tax;
    }
    // BR-C04: recalc amount when tax changes
    if (field === 'fh141_14') {
      const net = (row.fh141_13 as number) || 0;
      const tax = typeof value === 'number' ? value : 0;
      row.fh141_17 = net + tax;
    }

    updated[idx] = row;
    onChange(updated);
  }, [details, onChange]);

  const columns: ColumnsType<Partial<FH141>> = [
    {
      title: '項次',
      dataIndex: 'fh141_02',
      width: 50,
      align: 'center',
    },
    {
      title: '入庫單號',
      dataIndex: 'fh141_11',
      width: 130,
      render: (val, _, idx) => isEditing ? (
        <Input
          value={val}
          maxLength={20}
          onChange={e => handleChange(idx, 'fh141_11', e.target.value)}
        />
      ) : val,
    },
    {
      title: '入庫項次',
      dataIndex: 'fh141_15',
      width: 80,
      align: 'right',
      render: (val, _, idx) => isEditing ? (
        <InputNumber
          value={val}
          min={0}
          style={{ width: '100%' }}
          onChange={v => handleChange(idx, 'fh141_15', v ?? 0)}
        />
      ) : val,
    },
    {
      title: '折讓淨額',
      dataIndex: 'fh141_13',
      width: 120,
      align: 'right',
      render: (val, _, idx) => isEditing ? (
        <InputNumber
          value={val}
          precision={2}
          style={{ width: '100%' }}
          onChange={v => handleChange(idx, 'fh141_13', v ?? 0)}
        />
      ) : (val as number).toLocaleString('zh-TW', { minimumFractionDigits: 2 }),
    },
    {
      title: '稅額',
      dataIndex: 'fh141_14',
      width: 90,
      align: 'right',
      render: (val, _, idx) => isEditing ? (
        <InputNumber
          value={val}
          style={{ width: '100%' }}
          onChange={v => handleChange(idx, 'fh141_14', v ?? 0)}
        />
      ) : (val as number).toLocaleString('zh-TW'),
    },
    {
      title: '幣別',
      dataIndex: 'fh141_16',
      width: 60,
      render: (val, _, idx) => isEditing ? (
        <Input
          value={val}
          maxLength={6}
          style={{ width: '100%' }}
          onChange={e => handleChange(idx, 'fh141_16', e.target.value)}
        />
      ) : val,
    },
    {
      title: '折讓金額',
      dataIndex: 'fh141_17',
      width: 120,
      align: 'right',
      render: (val) => (val as number).toLocaleString('zh-TW', { minimumFractionDigits: 2 }),
    },
    {
      title: '客訴單號',
      dataIndex: 'fh141_18',
      width: 120,
      render: (val, _, idx) => isEditing ? (
        <Input
          value={val}
          maxLength={20}
          onChange={e => handleChange(idx, 'fh141_18', e.target.value)}
        />
      ) : val,
    },
    ...(isEditing ? [{
      title: '操作',
      width: 60,
      render: (_: any, __: any, idx: number) => (
        <Button size="small" danger onClick={() => handleDelete(idx)}>刪</Button>
      ),
    }] : []),
  ];

  return (
    <div className="pwh204-detail-grid">
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button type="dashed" onClick={handleAdd} disabled={details.length >= 99}>
            + 明細新增
          </Button>
        </Space>
      )}
      <Table
        rowKey={(_, idx) => String(idx)}
        dataSource={details}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 800 }}
        bordered
      />
    </div>
  );
};

export default DetailGrid;
